import { useEffect, useRef, useState } from "react";
import { vocabularize } from "src/Common/vocab";
import { toast } from "react-toastify";
import UserService from "src/Services/API/user.service";
import TimezoneDropdown from "./timezonedropdown";
import TimecardCycleDropdown from "./TimecardCycleDropdown";
import { getBranchLogoUrl } from "src/Common/common";
import PhoneNumberInput from "src/Components/Authentication/PhoneNumberInput";
import { isAdmin, isSuperAdmin } from "src/Common/function";

// One digit + one letter, excluding I/O (look like 1/0). e.g. "1H".
const PREFIX_REGEX = /^[0-9][A-HJ-NP-Z]$/;
// WO/PO start numbers are digits only; leading zeros are significant.
const START_NUMBER_REGEX = /^\d+$/;
// Six-digit hex only — the value is written straight into the printed
// document's styles.
const ACCENT_REGEX = /^#[0-9A-Fa-f]{6}$/;
// Shown in the swatch while no colour is chosen; matches the document's own
// neutral default so the picker opens on what the PO would actually print.
const DEFAULT_ACCENT_SWATCH = "#3F4A5A";

function AddModal({ branch, branches = [], onAddClicked, onUpateClicked, onCancelClicked }) {
  const currentUser = UserService.getCurrentUser();

  const superAdmin = isSuperAdmin();
  // The backend requires a prefix and WO/PO start numbers on every new branch,
  // so whoever creates one must be able to enter them — hiding the fields made
  // the create request fail validation. When editing, they stay super-admin-only
  // (they are locked once set), except on a legacy branch that never got one.
  const canEditNumbering = !branch || superAdmin || !branch.prefix;

  // The prefix locks once set, so regular admins can't change it after creation.
  // Super admins are exempt — they can still edit an existing branch's prefix.
  // (Branches with no prefix yet stay editable for everyone, e.g. ones created
  // before these fields existed.)
  const prefixLocked = !!(branch && branch.prefix) && !superAdmin;

  // Once a prefix is chosen the WO/PO start numbers are auto-determined to "001";
  // only a super admin may change them. So for everyone except a super admin the
  // start numbers are always locked (display-only, never an input).
  const woStartLocked = !superAdmin;
  const poStartLocked = !superAdmin;

  const [name, setName] = useState("");
  const [nameError, setNameError] = useState(false);
  const [prefix, setPrefix] = useState("");
  const [prefixError, setPrefixError] = useState(false);
  // New branches default the WO/PO start numbers to "001"; the effect below
  // overrides these for an existing branch being edited.
  const [woStartNumber, setWoStartNumber] = useState("001");
  const [woStartError, setWoStartError] = useState(false);
  const [poStartNumber, setPoStartNumber] = useState("001");
  const [poStartError, setPoStartError] = useState(false);
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneInvalid, setPhoneInvalid] = useState(false);
  const [timezone, setTimezone] = useState(null);
  const [timezoneError, setTimezoneError] = useState(false);
  const [timecardCycle, setTimecardCycle] = useState(null);
  const [timecardCycleError, setTimecardCycleError] = useState(false);
  // Empty means "no colour chosen": the document falls back to its default.
  const [documentAccent, setDocumentAccent] = useState("");
  const [documentAccentError, setDocumentAccentError] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [removeLogo, setRemoveLogo] = useState(false);

  const fileInputRef = useRef(null);

  const [yesButtonTitle, setYesButtonTitle] = useState("ADD");

  // A prefix must be unique within the company. Check the entered value against
  // existing branches (case-insensitive), excluding the branch being edited.
  const normalizedPrefix = prefix.trim().toUpperCase();
  const prefixTaken =
    !prefixLocked &&
    normalizedPrefix.length > 0 &&
    branches.some((b) => b.id !== branch?.id && (b.prefix || "").toUpperCase() === normalizedPrefix);

  useEffect(() => {
    if (branch) {
      setName(branch.name);
      setPrefix(branch.prefix || "");
      // Start numbers are auto-determined to "001" once a prefix is set, so fall
      // back to "001" for branches that never had an explicit value.
      setWoStartNumber(branch.woStartNumber || "001");
      setPoStartNumber(branch.poStartNumber || "001");
      setAddress(branch.address);
      setPhone(branch.phone || "");
      setTimezone(branch.timezone);
      setTimecardCycle(branch.timecardCycle ?? null);

      setDocumentAccent(branch.documentAccent || "");

      if (branch.logo) {
        setLogoPreview(getBranchLogoUrl(branch.id));
      }

      setYesButtonTitle("UPDATE");
    } else {
      setYesButtonTitle("ADD");
    }
  }, []);

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
    setRemoveLogo(false);
  };

  const addClicked = () => {
    // Timecard cycle and timezone are required: a branch drives timesheet
    // periods and scheduling off them, so both are flagged together rather than
    // one at a time.
    const missingTimecardCycle = timecardCycle === null || timecardCycle === undefined;
    const missingTimezone = !timezone;
    setTimecardCycleError(missingTimecardCycle);
    setTimezoneError(missingTimezone);

    if (!name.trim()) {
      setNameError(true);
      toast("Please enter a branch name");
      return;
    }
    if (phoneInvalid) {
      toast("Please enter a valid phone number");
      return;
    }
    if (missingTimecardCycle) {
      toast("Please select a timecard cycle");
      return;
    }
    if (missingTimezone) {
      toast("Please select a timezone");
      return;
    }
    if (documentAccent && !ACCENT_REGEX.test(documentAccent)) {
      setDocumentAccentError(true);
      toast("Document accent must be a six-digit hex colour, e.g. #2F7CC4");
      return;
    }

    if (branch) {
      branch.name = name;
      branch.address = address;
      branch.phone = phone;
      branch.timezone = timezone;
      branch.timecardCycle = timecardCycle;
      branch.documentAccent = documentAccent;

      // Numbering fields are admin-only. The prefix is locked once set; the WO/PO
      // start numbers are auto-determined from the prefix and editable by super
      // admins only. Locked fields are never re-sent.
      if (canEditNumbering) {
        if (!prefixLocked) {
          if (!PREFIX_REGEX.test(normalizedPrefix)) {
            setPrefixError(true);
            toast("Prefix must be one number and one letter (excluding I and O), e.g. 1H");
            return;
          }
          if (prefixTaken) {
            toast(`Prefix "${normalizedPrefix}" is already used by another branch`);
            return;
          }
          branch.prefix = normalizedPrefix;
        }
        if (!woStartLocked) {
          if (!START_NUMBER_REGEX.test(woStartNumber.trim())) {
            setWoStartError(true);
            toast(vocabularize("WO start number must contain digits only, e.g. 001"));
            return;
          }
          branch.woStartNumber = woStartNumber.trim();
        }
        if (!poStartLocked) {
          if (!START_NUMBER_REGEX.test(poStartNumber.trim())) {
            setPoStartError(true);
            toast("PO start number must contain digits only, e.g. 001");
            return;
          }
          branch.poStartNumber = poStartNumber.trim();
        }
      }

      onUpateClicked(branch, logoFile, removeLogo);
    } else {
      // No `company`: the backend derives the tenant from the caller's token, so
      // sending it here would create the branch under the signed-in user's own
      // company instead of the tenant a Super Admin has entered.
      const data = {
        name,
        address,
        phone,
        timezone,
        timecardCycle,
        documentAccent,
      };

      // Prefix and start numbers are mandatory at creation but only admins can
      // set them. (Branch creation is an admin action.)
      if (canEditNumbering) {
        if (!PREFIX_REGEX.test(normalizedPrefix)) {
          setPrefixError(true);
          toast("Prefix must be one number and one letter (excluding I and O), e.g. 1H");
          return;
        }
        if (prefixTaken) {
          toast(`Prefix "${normalizedPrefix}" is already used by another branch`);
          return;
        }
        if (!START_NUMBER_REGEX.test(woStartNumber.trim())) {
          setWoStartError(true);
          toast(vocabularize("WO start number must contain digits only, e.g. 001"));
          return;
        }
        if (!START_NUMBER_REGEX.test(poStartNumber.trim())) {
          setPoStartError(true);
          toast("PO start number must contain digits only, e.g. 001");
          return;
        }
        data.prefix = normalizedPrefix;
        data.woStartNumber = woStartNumber.trim();
        data.poStartNumber = poStartNumber.trim();
      }

      onAddClicked(data, logoFile);
    }
  };

  const cancelClicked = () => {
    onCancelClicked();
  };

  return (
    <div
      onClick={onCancelClicked}
      className="fixed w-screen h-screen left-0 top-0 bg-black bg-opacity-30 flex items-center justify-center z-30"
    >
      <div onClick={(e) => e.stopPropagation()} className="w-[448px] bg-white rounded-[6px] p-[18px] flex flex-col">
        <p className="text-[16px] font-OpenSansMedium font-[600] text-titleColor mb-5">
          {branch ? "EDIT BRANCH" : "ADD BRANCH"}
        </p>

        <div className="flex flex-col justify-between h-full">
          <div className="w-full mb-3">
            <p className="text-[12px] font-OpenSansMedium font-[600] text-titleColor mb-2">Logo</p>
            <div className="flex items-center gap-3">
              <div
                onClick={() => fileInputRef.current?.click()}
                className="w-[80px] h-[80px] border-dashed border-[2px] border-gray-300 rounded-[6px] flex items-center justify-center cursor-pointer overflow-hidden hover:border-blueColor transition-colors"
              >
                {logoPreview ? (
                  <img src={logoPreview} alt="Logo" className="w-full h-full object-contain" />
                ) : (
                  <span className="text-gray-400 text-[24px]">+</span>
                )}
              </div>
              <div className="flex flex-col">
                <span className="text-[11px] text-gray-500">Upload branch logo</span>
                <span className="text-[10px] text-gray-400">PNG, JPG (max 2MB)</span>
                {logoPreview && (
                  <span
                    onClick={() => {
                      setLogoPreview(null);
                      setLogoFile(null);
                      setRemoveLogo(true);
                      if (fileInputRef.current) fileInputRef.current.value = "";
                    }}
                    className="text-[10px] text-red-500 cursor-pointer mt-1 hover:underline"
                  >
                    Remove
                  </span>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg"
                onChange={handleLogoChange}
                className="hidden"
              />
            </div>
          </div>

          <div className="w-full mb-3">
            <p className="text-[12px] font-OpenSansMedium font-[600] text-titleColor mb-2">Document Accent</p>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={ACCENT_REGEX.test(documentAccent) ? documentAccent : DEFAULT_ACCENT_SWATCH}
                onChange={(e) => {
                  setDocumentAccent(e.target.value.toUpperCase());
                  setDocumentAccentError(false);
                }}
                className="w-[40px] h-[30px] p-0 border-solid border-[1px] border-gray-300 rounded-[5px] cursor-pointer bg-white"
                title="Pick the accent colour"
              />
              <div
                className={`w-[110px] h-[30px] border-solid border-[1px] rounded-[5px] flex items-center px-[10px] ${
                  documentAccentError ? "border-red-500" : ""
                }`}
              >
                <input
                  value={documentAccent}
                  onChange={(e) => {
                    setDocumentAccent(e.target.value.toUpperCase());
                    setDocumentAccentError(false);
                  }}
                  className="w-full text-inputEmailTitle text-[11px] font-[400] font-OpenSansMedium uppercase"
                  placeholder={DEFAULT_ACCENT_SWATCH}
                  maxLength={7}
                />
              </div>
              {documentAccent && (
                <span
                  onClick={() => {
                    setDocumentAccent("");
                    setDocumentAccentError(false);
                  }}
                  className="text-[10px] text-red-500 cursor-pointer hover:underline"
                >
                  Reset
                </span>
              )}
            </div>
            <p className="text-[10px] text-gray-400 mt-1">
              Used on this branch's purchase order PDFs. Leave blank for the default.
            </p>
          </div>

          <div className="h-[60px] w-full">
            <p className="text-[12px] font-OpenSansMedium font-[600] text-titleColor mb-2">Name</p>
            <div
              className={`w-full h-[30px] border-solid border-[1px] rounded-[5px] flex items-center justify-center px-[15px] ${nameError ? "border-red-500" : ""}`}
            >
              <input
                value={name}
                type="text"
                onChange={(e) => {
                  setName(e.target.value);
                  if (e.target.value.trim()) setNameError(false);
                }}
                className="w-full text-inputEmailTitle text-[11px] font-[400] font-OpenSansMedium"
                placeholder="Enter branch name"
              />
            </div>
            {nameError && (
              <p className="text-red-500 text-[10px] font-OpenSansMedium mt-1">Please enter a branch name</p>
            )}
          </div>

          {canEditNumbering && (
            <>
              <div className="w-full mt-3">
                <p className="text-[12px] font-OpenSansMedium font-[600] text-titleColor mb-2">Branch Prefix</p>
                <div
                  className={`w-full h-[30px] border-solid border-[1px] rounded-[5px] flex items-center px-[15px] ${
                    prefixError || prefixTaken ? "border-red-500" : ""
                  } ${prefixLocked ? "bg-gray-100" : ""}`}
                >
                  <input
                    value={prefix}
                    type="text"
                    maxLength={2}
                    disabled={prefixLocked}
                    onChange={(e) => {
                      const next = e.target.value.toUpperCase().slice(0, 2);
                      setPrefix(next);
                      if (PREFIX_REGEX.test(next)) setPrefixError(false);
                    }}
                    className={`w-full text-inputEmailTitle text-[11px] font-[400] font-OpenSansMedium ${
                      prefixLocked ? "text-gray-500 cursor-not-allowed" : ""
                    }`}
                    placeholder="e.g. 1H"
                  />
                </div>
                {prefixLocked ? (
                  <p className="text-gray-400 text-[10px] font-OpenSansMedium mt-1">
                    The prefix is locked once set. Only a super admin can change it.
                  </p>
                ) : prefixTaken ? (
                  <p className="text-red-500 text-[10px] font-OpenSansMedium mt-1">
                    Prefix "{normalizedPrefix}" is already used by another branch
                  </p>
                ) : prefixError ? (
                  <p className="text-red-500 text-[10px] font-OpenSansMedium mt-1">
                    One number and one letter (excluding I and O), e.g. 1H
                  </p>
                ) : (
                  <p className="text-gray-400 text-[10px] font-OpenSansMedium mt-1">
                    One number and one letter (excluding I and O). {vocabularize("This sets WO and PO numbers.")}
                  </p>
                )}
              </div>

              {woStartLocked ? (
                // Locked: auto-determined by the prefix. Label and value on one
                // line, no input (editing is only for super admins).
                <div className="w-full mt-3 flex items-center">
                  <p className="text-[12px] font-OpenSansMedium font-[600] text-titleColor">{vocabularize("WO Start Number")}:&nbsp;</p>
                  <span className="text-[11px] font-[700] font-OpenSansMedium text-blueColor bg-blueColor/10 rounded-[4px] px-[6px] py-[2px]">
                    {(PREFIX_REGEX.test(prefix) ? prefix : "") + woStartNumber}
                  </span>
                </div>
              ) : (
                <div className="w-full mt-3">
                  <p className="text-[12px] font-OpenSansMedium font-[600] text-titleColor mb-2">{vocabularize("WO Start Number")}</p>
                  <div
                    className={`w-full h-[30px] border-solid border-[1px] rounded-[5px] flex items-center px-[15px] ${
                      woStartError ? "border-red-500" : ""
                    }`}
                  >
                    {PREFIX_REGEX.test(prefix) && (
                      <span className="text-[11px] font-[600] font-OpenSansMedium text-titleColor">{prefix}</span>
                    )}
                    <input
                      value={woStartNumber}
                      type="text"
                      inputMode="numeric"
                      onChange={(e) => {
                        const next = e.target.value.replace(/\D/g, "");
                        setWoStartNumber(next);
                        if (START_NUMBER_REGEX.test(next)) setWoStartError(false);
                      }}
                      className="w-full text-inputEmailTitle text-[11px] font-[400] font-OpenSansMedium"
                    />
                  </div>
                  {woStartError ? (
                    <p className="text-red-500 text-[10px] font-OpenSansMedium mt-1">Digits only, e.g. 001</p>
                  ) : null}
                </div>
              )}

              {poStartLocked ? (
                // Locked: auto-determined by the prefix. Label and value on one
                // line, no input (editing is only for super admins).
                <div className="w-full mt-3 flex items-center">
                  <p className="text-[12px] font-OpenSansMedium font-[600] text-titleColor">PO Start Number:&nbsp;</p>
                  <span className="text-[11px] font-[700] font-OpenSansMedium text-blueColor bg-blueColor/10 rounded-[4px] px-[6px] py-[2px]">
                    {(PREFIX_REGEX.test(prefix) ? `P${prefix}` : "") + poStartNumber}
                  </span>
                </div>
              ) : (
                <div className="w-full mt-3">
                  <p className="text-[12px] font-OpenSansMedium font-[600] text-titleColor mb-2">PO Start Number</p>
                  <div
                    className={`w-full h-[30px] border-solid border-[1px] rounded-[5px] flex items-center px-[15px] ${
                      poStartError ? "border-red-500" : ""
                    }`}
                  >
                    {PREFIX_REGEX.test(prefix) && (
                      <span className="text-[11px] font-[600] font-OpenSansMedium text-titleColor">P{prefix}</span>
                    )}
                    <input
                      value={poStartNumber}
                      type="text"
                      inputMode="numeric"
                      onChange={(e) => {
                        const next = e.target.value.replace(/\D/g, "");
                        setPoStartNumber(next);
                        if (START_NUMBER_REGEX.test(next)) setPoStartError(false);
                      }}
                      className="w-full text-inputEmailTitle text-[11px] font-[400] font-OpenSansMedium"
                    />
                  </div>
                  {poStartError ? (
                    <p className="text-red-500 text-[10px] font-OpenSansMedium mt-1">Digits only, e.g. 0001</p>
                  ) : null}
                </div>
              )}
            </>
          )}

          <div className="h-[60px] w-full mt-3">
            <p className="text-[12px] font-OpenSansMedium font-[600] text-titleColor mb-2">Address</p>
            <div className="w-full h-[30px] border-solid border-[1px] rounded-[5px] flex items-center justify-center px-[15px]">
              <input
                value={address}
                type="text"
                onChange={(e) => setAddress(e.target.value)}
                className="w-full text-inputEmailTitle text-[11px] font-[400] font-OpenSansMedium"
                placeholder="Enter branch address"
              />
            </div>
          </div>

          <div className="h-[60px] w-full mt-3">
            <PhoneNumberInput
              label="Phone"
              placeholder="Enter branch phone"
              value={phone}
              onChange={setPhone}
              onInvalidChange={setPhoneInvalid}
            />
          </div>

          <div className="h-[60px] w-full mt-3">
            <p className="text-[12px] font-OpenSansMedium font-[600] text-titleColor mb-2">Timecard Cycle</p>
            <TimecardCycleDropdown
              timecardCycle={timecardCycle}
              timecardCycleSelected={(cycle) => {
                setTimecardCycle(cycle);
                setTimecardCycleError(false);
              }}
              isEditing={!!branch}
              hasError={timecardCycleError}
            />
            {timecardCycleError && (
              <p className="text-red-500 text-[10px] font-OpenSansMedium mt-1">Please select a timecard cycle</p>
            )}
          </div>

          <div className="h-[60px] w-full mt-3">
            <p className="text-[12px] font-OpenSansMedium font-[600] text-titleColor mb-2">Timezone</p>
            <TimezoneDropdown
              timezone={timezone}
              timezoneSelected={(zone) => {
                setTimezone(zone);
                setTimezoneError(false);
              }}
              hasError={timezoneError}
            />
            {timezoneError && (
              <p className="text-red-500 text-[10px] font-OpenSansMedium mt-1">Please select a timezone</p>
            )}
          </div>

          <div className=" w-full mt-3">
            <div className="flex items-center justify-between gap-4">
              <div
                onClick={cancelClicked}
                className="w-[50%] h-[30px] text-[10px] rounded-[6px] border-[1px] border-subTitleColor flex items-center justify-center text-subTitleColor cursor-pointer"
              >
                CANCEL
              </div>
              <div
                onClick={prefixTaken ? undefined : addClicked}
                className={`w-[50%] h-[30px] text-[10px] rounded-[6px] flex items-center justify-center text-white ${
                  prefixTaken ? "bg-blueColor/50 cursor-not-allowed" : "bg-blueColor cursor-pointer"
                }`}
              >
                {yesButtonTitle}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddModal;
